<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <router-link :to="{ name: 'Dashboard' }" class="mb-0 main-identifier fw-semibold text-decoration-none"
        title="Go back to Dashboard">
        Dashboard
      </router-link>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">Special Approval Types</p>
    </div>

    <div class="d-flex justify-content-center">
      <div class="form-outer" style="max-width: 1400px;">
        <div class="view-item position-relative">

          <div class="form-inner">
            <div class="form-header d-flex justify-content-between align-items-center report">
              <div>
                <h1 class="mb-0">Special Approval Types</h1>
                <p class="mb-0 small">View and manage special approval types</p>
              </div>
            </div>

            <div class="form-body">

              <!-- Filters -->
              <div class="filters-section mb-4">
                <div class="row g-3">
                  <div class="col-md-3">
                    <label class="form-label small fw-semibold">Approval Type:</label>
                    <select class="form-select form-select-sm" v-model="filters.approvalType">
                      <option value="">All</option>
                      <option value="global">Global Approval</option>
                      <option value="monthly">Monthly Approval</option>
                    </select>
                  </div>

                  <div class="col-md-3 d-flex align-items-end">
                    <div class="w-100">
                      <label class="form-label small fw-semibold invisible">Action</label>
                      <button class="btn btn-sm btn-outline-secondary w-100" @click="clearFilters">
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Table Controls -->
              <div v-if="!loading" class="table-controls">
                <div class="table-controls-top">
                  <div class="table-controls-left">
                    <div class="entries-control">
                      <span>Show</span>
                      <select class="form-select form-select-sm" v-model.number="pageLength">
                        <option :value="10">10</option>
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                      </select>
                      <span>Entries</span>
                    </div>

                    <div class="search-box" style="max-width: 500px;">
                      <span class="material-symbols-outlined search-icon">search</span>
                      <input type="search" v-model="search" class="form-control form-control-sm search-input"
                        placeholder="Search by name or description..." />
                    </div>
                  </div>

                  <div class="export-links">
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToPDF()">Export to PDF</a>
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToExcel()">Export to Excel</a>
                  </div>
                </div>
              </div>

              <!-- Table -->
              <div v-if="loading" class="loading-state text-center py-5">
                <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                <p class="mt-2 mb-0 text-muted">Loading special approval types...</p>
              </div>

              <div v-else class="table-wrapper">
                <div class="table-container">
                  <table class="responsive-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Name</th>
                        <th>Description</th>
                        <th class="text-end">Maximum Amount</th>
                        <th>Approval Type</th>
                        <th>Creation Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="paginatedItems.length === 0">
                        <td colspan="6" class="text-center text-muted py-4">No special approval types to display.</td>
                      </tr>
                      <tr v-for="(type, index) in paginatedItems" :key="type.id"
                        :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                        <td data-label="S/N" class="serial-no text-center">
                          {{ (currentPage - 1) * pageLength + index + 1 }}
                        </td>
                        <td data-label="Name" class="primary-col">
                          <router-link :to="{ name: 'ViewSpecialApprovalType', params: { id: type.id } }"
                            class="view-link text-decoration-underline">
                            {{ type.name }}
                          </router-link>
                        </td>
                        <td data-label="Description" class="secondary-col">{{ type.description }}</td>
                        <td data-label="Maximum Amount" class="secondary-col text-end">{{ formatCurrency(type.maxAmount) }}</td>
                        <td data-label="Approval Type" class="secondary-col">{{ type.approvalTypeLabel }}</td>
                        <td data-label="Creation Date" class="secondary-col">{{ formatDate(type.createdDate) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Pagination Summary and Actions -->
              <div class="pagination-info">
                <small>
                  Showing {{ (currentPage - 1) * pageLength + 1 }} to
                  {{ Math.min(currentPage * pageLength, filteredItems.length) }} of
                  {{ filteredItems.length }} entries
                </small>

                <nav class="pagination-nav">
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">Previous</a>
                    </li>
                    <li v-for="page in totalPages" :key="page" class="page-item"
                      :class="{ active: currentPage === page }">
                      <a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">Next</a>
                    </li>
                  </ul>
                </nav>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axios from 'axios'
import Cookies from 'js-cookie'
import { debounce } from 'lodash'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import ExcelJS from 'exceljs'
import DashboardLayout from '../../layout/DashboardLayout.vue'
import '@/assets/css/table.css'
import '@/assets/css/responsive-table.css'

const root_api_url = process.env.VUE_APP_API_ROOT_URL

const approvalTypes = ref([])
const loading = ref(true)
const search = ref('')
const pageLength = ref(10)
const currentPage = ref(1)

const filters = ref({
  approvalType: '',
})

const debouncedSearch = ref('')
watch(search, debounce((val) => {
  debouncedSearch.value = val
}, 300))

watch(pageLength, () => {
  currentPage.value = 1
})

watch(filters, () => {
  currentPage.value = 1
}, { deep: true })

const fetchSpecialApprovalTypes = async () => {
  const token = Cookies.get('auth_token')
  loading.value = true
  try {
    const { data } = await axios.get(`${root_api_url}/appraisal/spec-approval-type/all`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    approvalTypes.value = (Array.isArray(data) ? data : []).map(item => ({
      id: item.id,
      name: item.name,
      description: item.description,
      maxAmount: item.maxAmount,
      createdDate: item.createdDate,
      globalApprovalInd: item.globalApprovalInd,
      monthlyApprovalInd: item.monthlyApprovalInd,
      approvalTypeLabel: item.globalApprovalInd ? 'Global Approval' : item.monthlyApprovalInd ? 'Monthly Approval' : 'N/A',
    }))
  } catch (error) {
    console.error('Failed to fetch special approval types:', error)
    approvalTypes.value = []
  } finally {
    loading.value = false
  }
}

const filteredItems = computed(() => {
  let filtered = approvalTypes.value

  if (debouncedSearch.value) {
    const term = debouncedSearch.value.toLowerCase()
    filtered = filtered.filter(item =>
      (item.name || '').toLowerCase().includes(term) ||
      (item.description || '').toLowerCase().includes(term)
    )
  }

  if (filters.value.approvalType) {
    filtered = filtered.filter(item =>
      filters.value.approvalType === 'global' ? item.globalApprovalInd : item.monthlyApprovalInd
    )
  }

  return filtered
})

const paginatedItems = computed(() => {
  const start = (currentPage.value - 1) * pageLength.value
  return filteredItems.value.slice(start, start + pageLength.value)
})

const totalPages = computed(() => {
  return Math.ceil(filteredItems.value.length / pageLength.value)
})

const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

const clearFilters = () => {
  filters.value = { approvalType: '' }
  search.value = ''
}

const formatDate = (dateString) => {
  if (!dateString) return 'N/A'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const formatCurrency = (val) => {
  if (val === '' || val === null || val === undefined) return 'N/A'
  return '₦' + Number(parseFloat(val).toFixed(2)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const exportToPDF = () => {
  const doc = new jsPDF()
  autoTable(doc, {
    head: [['S/N', 'Name', 'Description', 'Maximum Amount', 'Approval Type', 'Creation Date']],
    body: approvalTypes.value.map((item, index) => [
      index + 1,
      item.name,
      item.description,
      formatCurrency(item.maxAmount),
      item.approvalTypeLabel,
      formatDate(item.createdDate),
    ])
  })
  doc.save('special-approval-types.pdf')
}

const exportToExcel = async () => {
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('Special Approval Types')
  worksheet.addRow(['S/N', 'Name', 'Description', 'Maximum Amount', 'Approval Type', 'Creation Date'])

  approvalTypes.value.forEach((item, index) => {
    worksheet.addRow([
      index + 1,
      item.name,
      item.description,
      item.maxAmount,
      item.approvalTypeLabel,
      formatDate(item.createdDate),
    ])
  })

  worksheet.getRow(1).font = { bold: true }

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'special-approval-types.xlsx'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

onMounted(() => {
  document.title = 'Special Approval Types'
  fetchSpecialApprovalTypes()
})
</script>

<style scoped>
.view-item,
.form-inner,
.form-body {
  overflow: visible !important;
}

.primary-col {
  font-weight: 500;
}

.row-even {
  background-color: #ffffff;
}

.row-odd {
  background-color:var(--table-bg-head);
}

.filters-section {
  background-color:var(--table-bg-head);
  padding: 1.5rem;
  border-radius: 0.375rem;
  border: 1px solid var(--table-border);
}

@media (max-width: 768px) {
  .filters-section .row {
    gap: 0.75rem;
  }

  .table-controls-top {
    flex-direction: column;
    gap: 1rem;
  }

  .export-links {
    width: 100%;
    display: flex;
    gap: 1rem;
  }

  .export-link {
    flex: 1;
    text-align: center;
  }
}
</style>
